import { ArrowUpRight } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  STAR_DETAIL_OPTIONS,
  useFaceMatchingIdentities,
  useFaceMatchingIdentity,
} from '@/features/face-matching/hooks/useFaceMatching'
import { IdentityMatches } from '@/features/face-matching/IdentityMatches'
import { survivorNamedBy, useMergedInto } from '@/features/identities/hooks/useMergedInto'
import { FaceIdentityRows } from './FaceIdentityRows'

/**
 * *Photo matching* tab: identities with pending face suggestions on the left,
 * the selected identity's reference strip and paged candidate table below.
 * Fetching lives in `features/face-matching`; everything it renders is pure.
 */
export function PhotoMatchingPanel({
  caseId,
  onOpenIdentity,
}: {
  caseId: string
  onOpenIdentity?: (personId: string) => void
}) {
  const { t } = useTranslation()
  const base = 'cases.investigate.entityResolution.photoMatching'
  const [personId, setPersonId] = useState<string | null>(null)

  const identities = useFaceMatchingIdentities(caseId)
  // The same query the star inside `IdentityMatches` reads, so the heading
  // costs nothing beyond the cache entry that is already there.
  const star = useFaceMatchingIdentity(caseId, personId, STAR_DETAIL_OPTIONS)
  // The selected identity was merged into another one: the selection follows the
  // survivor, which now holds its suggestions (ADR-0052). A row the list has not
  // dropped yet is followed as the star's read names the survivor (ADR-0053).
  const mergedInto = useMergedInto(caseId, personId, survivorNamedBy(star))
  if (mergedInto) setPersonId(mergedInto)

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader className="max-md:px-4">
          <CardTitle>{t(`${base}.title`)}</CardTitle>
        </CardHeader>
        <CardContent className="px-0">
          {identities.isLoading ? (
            <div className="flex flex-col gap-2 px-4">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : identities.isError ? (
            <p className="px-4 text-sm text-destructive">{t(`${base}.error`)}</p>
          ) : (
            <FaceIdentityRows
              identities={identities.data?.identities ?? []}
              selectedPersonId={personId}
              onSelect={setPersonId}
            />
          )}
        </CardContent>
      </Card>

      {personId ? (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-3 max-md:px-4">
            <CardTitle>{star.data?.display_name ?? t(`${base}.unknownIdentity`)}</CardTitle>
            {onOpenIdentity ? (
              <Button variant="outline" size="sm" onClick={() => onOpenIdentity(personId)}>
                {t('identity.openProfile')}
                <ArrowUpRight className="size-4" />
              </Button>
            ) : null}
          </CardHeader>
          <CardContent className="max-md:px-4">
            {/* Keyed: the candidates on screen and the decision in flight are one
                identity's, and never carry over to the next one selected. */}
            <IdentityMatches key={personId} caseId={caseId} personId={personId} references />
          </CardContent>
        </Card>
      ) : null}
    </div>
  )
}
